
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';

export const getPrayers = createAsyncThunk(
    'prayer/getPrayers',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.PRAYERS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Prayers retrieved successfully',
                    data: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch prayers',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch prayers';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch prayers';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getPrayer = createAsyncThunk(
    'prayer/getPrayer',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.PRAYER(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Prayer retrieved successfully',
                    data: data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch prayer',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch prayer';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch prayer';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const createPrayer = createAsyncThunk(
    'prayer/createPrayer',
    async (prayerData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.PRAYER_CREATE, prayerData);
            const { status, message, data } = response.data;

            if (status === 201 || status === 200) {
                return {
                    success: true,
                    message: message || 'Prayer created successfully',
                    prayer: data?.data || data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to create prayer',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to create prayer';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to create prayer';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const updatePrayer = createAsyncThunk(
    'prayer/updatePrayer',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.put(API_ENDPOINTS.PRAYER_UPDATE(id), data);
            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Prayer updated successfully',
                    prayer: responseData?.data || responseData || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update prayer',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to update prayer';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to update prayer';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const deletePrayer = createAsyncThunk(
    'prayer/deletePrayer',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.delete(API_ENDPOINTS.PRAYER_DELETE(id));
            const { status, message } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Prayer deleted successfully',
                    id: id
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to delete prayer',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to delete prayer';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to delete prayer';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const markPrayerPrayed = createAsyncThunk(
    'prayer/markPrayerPrayed',
    async (id, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.PRAYER_MARK_PRAYED(id));
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Prayer marked as prayed successfully',
                    prayer: data?.data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to mark prayer as prayed',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to mark prayer as prayed';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to mark prayer as prayed';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const respondToPrayer = createAsyncThunk(
    'prayer/respondToPrayer',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.PRAYER_RESPOND(id), data);
            const { status, message, data: responseData } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Response sent successfully',
                    prayer: responseData?.data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to send response',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to send response';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to send response';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const escalatePrayer = createAsyncThunk(
    'prayer/escalatePrayer',
    async ({ id, notes = '' }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.PRAYER_ESCALATE(id), { notes });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Prayer escalated successfully',
                    prayer: data?.data || null
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to escalate prayer',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to escalate prayer';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to escalate prayer';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const getPrayerStats = createAsyncThunk(
    'prayer/getStats',
    async (params = {}, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.PRAYER_STATS, { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Prayer statistics retrieved successfully',
                    stats: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch prayer statistics',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch prayer statistics';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch prayer statistics';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const sendPrayerDigest = createAsyncThunk(
    'prayer/sendDigest',
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.post(API_ENDPOINTS.PRAYER_SEND_DIGEST);
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Prayer digest sent successfully',
                    result: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to send prayer digest',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to send prayer digest';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to send prayer digest';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const exportPrayers = createAsyncThunk(
    'prayer/export',
    async ({ type, params = {} }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.PRAYER_EXPORT(type), { params });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Export data generated successfully',
                    exportData: data || {},
                    type: type
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to export data',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to export data';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to export data';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// ============================================
// SETTINGS THUNKS
// ============================================

export const getPrayerSettings = createAsyncThunk(
    'prayer/getSettings',
    async (_, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.get(API_ENDPOINTS.PRAYER_SETTINGS);
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Settings retrieved successfully',
                    settings: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to fetch settings',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to fetch settings';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to fetch settings';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const updatePrayerSettings = createAsyncThunk(
    'prayer/updateSettings',
    async (settingsData, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.put(API_ENDPOINTS.PRAYER_SETTINGS_UPDATE, settingsData);
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Settings updated successfully',
                    settings: data || {}
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update settings',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to update settings';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to update settings';
                
                if (serverData.errors) {
                    const errorMessages = Object.values(serverData.errors).flat().join(', ');
                    errorMessage = errorMessages || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

export const updateAcknowledgmentTemplate = createAsyncThunk(
    'prayer/updateTemplate',
    async ({ template }, { rejectWithValue }) => {
        try {
            const token = localStorage.getItem('access_token');
            if (!token) {
                throw new Error('No token available');
            }

            const response = await apiClient.put(API_ENDPOINTS.PRAYER_TEMPLATE_UPDATE, { acknowledgment_template: template });
            const { status, message, data } = response.data;

            if (status === 200) {
                return {
                    success: true,
                    message: message || 'Template updated successfully',
                    template: data?.acknowledgment_template || template
                };
            } else {
                return rejectWithValue({
                    message: message || 'Failed to update template',
                    status: status,
                    apiResponse: response.data
                });
            }
        } catch (error) {
            let errorMessage = 'Failed to update template';
            let statusCode = 500;

            if (error.response) {
                statusCode = error.response.status;
                const serverData = error.response.data;
                errorMessage = serverData.message || serverData.error || 'Failed to update template';
            } else if (error.request) {
                errorMessage = 'Network error. Please check your connection.';
            } else {
                errorMessage = error.message || 'An unexpected error occurred';
            }

            return rejectWithValue({
                message: errorMessage,
                status: statusCode,
                apiResponse: error.response?.data
            });
        }
    }
);

// ============================================
// INITIAL STATE
// ============================================

const initialState = {
    prayers: [],
    currentPrayer: null,
    settings: {
        auto_acknowledge: true,
        allow_anonymous: true,
        alert_pastor_for_urgent: true,
        send_weekly_digest: false,
        flag_crisis_keywords: true,
        acknowledgment_template: "Dear {FirstName}, we have received your prayer request and our team is lifting you up right now. God hears and answers prayers. — Grace Chapel 🙏",
        crisis_keywords: ['suicide', 'death', 'cancer', 'emergency', 'hospital', 'accident', 'crisis'],
        weekly_digest_template: "Dear Members, please join us in prayer for these requests:\n\n{prayer_list}\n\nThank you for praying! 🙏 — Grace Chapel",
    },
    stats: {
        total: 0,
        pending: 0,
        prayed: 0,
        responded: 0,
        urgent: 0,
        escalated: 0,
        anonymous: 0,
        weekly_data: [],
        group_stats: [],
        stats: {
            total_requests: 0,
            pending: 0,
            prayed: 0,
            responded: 0,
            urgent: 0,
            escalated: 0,
            anonymous: 0,
            weekly_data: [],
            group_stats: []
        }
    },
    exportData: null,
    digestResult: null,
    pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0
    },
    isLoading: false,
    error: null,
    successMessage: null,
    apiMessage: null,
};

// ============================================
// SLICE
// ============================================

const prayerSlice = createSlice({
    name: 'prayer',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearSuccessMessage: (state) => {
            state.successMessage = null;
        },
        clearApiMessage: (state) => {
            state.apiMessage = null;
        },
        clearPrayerState: (state) => {
            state.prayers = [];
            state.currentPrayer = null;
            state.settings = initialState.settings;
            state.stats = initialState.stats;
            state.exportData = null;
            state.digestResult = null;
            state.pagination = initialState.pagination;
            state.error = null;
            state.successMessage = null;
            state.apiMessage = null;
            state.isLoading = false;
        },
        clearCurrentPrayer: (state) => {
            state.currentPrayer = null;
        },
        clearExportData: (state) => {
            state.exportData = null;
        },
        clearDigestResult: (state) => {
            state.digestResult = null;
        },
        clearSettings: (state) => {
            state.settings = initialState.settings;
        },
        updateSettingsLocal: (state, action) => {
            state.settings = { ...state.settings, ...action.payload };
        },
    },
    extraReducers: (builder) => {
        builder
            // ===== GET PRAYERS =====
            .addCase(getPrayers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getPrayers.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                const responseData = payload.data || {};
                state.prayers = responseData.data || [];
                state.pagination = responseData.meta || initialState.pagination;
                if (responseData.meta?.stats) {
                    state.stats = { ...state.stats, ...responseData.meta.stats };
                }
                state.successMessage = payload.message || 'Prayers retrieved successfully';
                state.apiMessage = payload.message || 'Prayers retrieved successfully';
                state.error = null;
            })
            .addCase(getPrayers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch prayers';
                state.apiMessage = action.payload?.message || 'Failed to fetch prayers';
                state.successMessage = null;
                state.prayers = [];
                state.pagination = initialState.pagination;
            })

            // ===== GET PRAYER =====
            .addCase(getPrayer.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getPrayer.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.currentPrayer = payload.data || null;
                state.successMessage = payload.message || 'Prayer retrieved successfully';
                state.apiMessage = payload.message || 'Prayer retrieved successfully';
                state.error = null;
            })
            .addCase(getPrayer.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch prayer';
                state.apiMessage = action.payload?.message || 'Failed to fetch prayer';
                state.successMessage = null;
                state.currentPrayer = null;
            })

            // ===== CREATE PRAYER =====
            .addCase(createPrayer.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(createPrayer.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Prayer created successfully';
                state.apiMessage = payload.message || 'Prayer created successfully';
                state.error = null;
                if (payload.prayer) {
                    state.prayers = [payload.prayer, ...state.prayers];
                }
            })
            .addCase(createPrayer.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to create prayer';
                state.apiMessage = action.payload?.message || 'Failed to create prayer';
                state.successMessage = null;
            })

            // ===== UPDATE PRAYER =====
            .addCase(updatePrayer.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updatePrayer.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Prayer updated successfully';
                state.apiMessage = payload.message || 'Prayer updated successfully';
                state.error = null;
                if (payload.prayer) {
                    state.prayers = state.prayers.map(p => 
                        p.id === payload.prayer.id ? payload.prayer : p
                    );
                    if (state.currentPrayer?.id === payload.prayer.id) {
                        state.currentPrayer = payload.prayer;
                    }
                }
            })
            .addCase(updatePrayer.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update prayer';
                state.apiMessage = action.payload?.message || 'Failed to update prayer';
                state.successMessage = null;
            })

            // ===== DELETE PRAYER =====
            .addCase(deletePrayer.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(deletePrayer.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Prayer deleted successfully';
                state.apiMessage = payload.message || 'Prayer deleted successfully';
                state.error = null;
                if (payload.id) {
                    state.prayers = state.prayers.filter(p => p.id !== payload.id);
                    if (state.currentPrayer?.id === payload.id) {
                        state.currentPrayer = null;
                    }
                }
            })
            .addCase(deletePrayer.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to delete prayer';
                state.apiMessage = action.payload?.message || 'Failed to delete prayer';
                state.successMessage = null;
            })

            // ===== MARK PRAYED =====
            .addCase(markPrayerPrayed.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(markPrayerPrayed.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Prayer marked as prayed successfully';
                state.apiMessage = payload.message || 'Prayer marked as prayed successfully';
                state.error = null;
                if (payload.prayer) {
                    state.prayers = state.prayers.map(p => 
                        p.id === payload.prayer.id ? payload.prayer : p
                    );
                    if (state.currentPrayer?.id === payload.prayer.id) {
                        state.currentPrayer = payload.prayer;
                    }
                }
            })
            .addCase(markPrayerPrayed.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to mark prayer as prayed';
                state.apiMessage = action.payload?.message || 'Failed to mark prayer as prayed';
                state.successMessage = null;
            })

            // ===== RESPOND =====
            .addCase(respondToPrayer.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(respondToPrayer.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Response sent successfully';
                state.apiMessage = payload.message || 'Response sent successfully';
                state.error = null;
                if (payload.prayer) {
                    state.prayers = state.prayers.map(p => 
                        p.id === payload.prayer.id ? payload.prayer : p
                    );
                    if (state.currentPrayer?.id === payload.prayer.id) {
                        state.currentPrayer = payload.prayer;
                    }
                }
            })
            .addCase(respondToPrayer.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to send response';
                state.apiMessage = action.payload?.message || 'Failed to send response';
                state.successMessage = null;
            })

            // ===== ESCALATE =====
            .addCase(escalatePrayer.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(escalatePrayer.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Prayer escalated successfully';
                state.apiMessage = payload.message || 'Prayer escalated successfully';
                state.error = null;
                if (payload.prayer) {
                    state.prayers = state.prayers.map(p => 
                        p.id === payload.prayer.id ? payload.prayer : p
                    );
                    if (state.currentPrayer?.id === payload.prayer.id) {
                        state.currentPrayer = payload.prayer;
                    }
                }
            })
            .addCase(escalatePrayer.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to escalate prayer';
                state.apiMessage = action.payload?.message || 'Failed to escalate prayer';
                state.successMessage = null;
            })

            // ===== GET STATS =====
            .addCase(getPrayerStats.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getPrayerStats.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.stats = { ...state.stats, ...(payload.stats || {}) };
                state.successMessage = payload.message || 'Prayer statistics retrieved successfully';
                state.apiMessage = payload.message || 'Prayer statistics retrieved successfully';
                state.error = null;
            })
            .addCase(getPrayerStats.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch prayer statistics';
                state.apiMessage = action.payload?.message || 'Failed to fetch prayer statistics';
                state.successMessage = null;
            })

            // ===== SEND DIGEST =====
            .addCase(sendPrayerDigest.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(sendPrayerDigest.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.successMessage = payload.message || 'Prayer digest sent successfully';
                state.apiMessage = payload.message || 'Prayer digest sent successfully';
                state.error = null;
                state.digestResult = payload.result || null;
            })
            .addCase(sendPrayerDigest.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to send prayer digest';
                state.apiMessage = action.payload?.message || 'Failed to send prayer digest';
                state.successMessage = null;
                state.digestResult = null;
            })

            // ===== EXPORT =====
            .addCase(exportPrayers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(exportPrayers.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.exportData = payload.exportData || null;
                state.successMessage = payload.message || 'Export data generated successfully';
                state.apiMessage = payload.message || 'Export data generated successfully';
                state.error = null;
            })
            .addCase(exportPrayers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to export data';
                state.apiMessage = action.payload?.message || 'Failed to export data';
                state.successMessage = null;
                state.exportData = null;
            })

            // ===== GET SETTINGS =====
            .addCase(getPrayerSettings.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(getPrayerSettings.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.settings = { ...state.settings, ...(payload.settings || {}) };
                state.successMessage = payload.message || 'Settings retrieved successfully';
                state.apiMessage = payload.message || 'Settings retrieved successfully';
                state.error = null;
            })
            .addCase(getPrayerSettings.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to fetch settings';
                state.apiMessage = action.payload?.message || 'Failed to fetch settings';
                state.successMessage = null;
            })

            // ===== UPDATE SETTINGS =====
            .addCase(updatePrayerSettings.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updatePrayerSettings.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.settings = { ...state.settings, ...(payload.settings || {}) };
                state.successMessage = payload.message || 'Settings updated successfully';
                state.apiMessage = payload.message || 'Settings updated successfully';
                state.error = null;
            })
            .addCase(updatePrayerSettings.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update settings';
                state.apiMessage = action.payload?.message || 'Failed to update settings';
                state.successMessage = null;
            })

            // ===== UPDATE TEMPLATE =====
            .addCase(updateAcknowledgmentTemplate.pending, (state) => {
                state.isLoading = true;
                state.error = null;
                state.successMessage = null;
                state.apiMessage = null;
            })
            .addCase(updateAcknowledgmentTemplate.fulfilled, (state, action) => {
                state.isLoading = false;
                const payload = action.payload || {};
                state.settings.acknowledgment_template = payload.template || state.settings.acknowledgment_template;
                state.successMessage = payload.message || 'Template updated successfully';
                state.apiMessage = payload.message || 'Template updated successfully';
                state.error = null;
            })
            .addCase(updateAcknowledgmentTemplate.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload?.message || 'Failed to update template';
                state.apiMessage = action.payload?.message || 'Failed to update template';
                state.successMessage = null;
            });
    },
});

export const {
    clearError,
    clearSuccessMessage,
    clearApiMessage,
    clearPrayerState,
    clearCurrentPrayer,
    clearExportData,
    clearDigestResult,
    clearSettings,
    updateSettingsLocal,
} = prayerSlice.actions;

export const selectPrayers = (state) => state.prayer?.prayers || [];
export const selectCurrentPrayer = (state) => state.prayer?.currentPrayer || null;
export const selectPrayerStats = (state) => state.prayer?.stats || initialState.stats;
export const selectPrayerPagination = (state) => state.prayer?.pagination || initialState.pagination;
export const selectPrayerLoading = (state) => state.prayer?.isLoading || false;
export const selectPrayerError = (state) => state.prayer?.error || null;
export const selectPrayerSuccess = (state) => state.prayer?.successMessage || null;
export const selectPrayerApiMessage = (state) => state.prayer?.apiMessage || null;
export const selectPrayerExportData = (state) => state.prayer?.exportData || null;
export const selectPrayerDigestResult = (state) => state.prayer?.digestResult || null;

export const selectPrayerSettings = (state) => state.prayer?.settings || initialState.settings;
export const selectAcknowledgmentTemplate = (state) => state.prayer?.settings?.acknowledgment_template || initialState.settings.acknowledgment_template;
export const selectCrisisKeywords = (state) => state.prayer?.settings?.crisis_keywords || initialState.settings.crisis_keywords;
export const selectWeeklyDigestTemplate = (state) => state.prayer?.settings?.weekly_digest_template || initialState.settings.weekly_digest_template;

export const selectPrayerStatsSummary = (state) => {
    const stats = selectPrayerStats(state);
    return {
        total: stats.total || 0,
        pending: stats.pending || 0,
        prayed: stats.prayed || 0,
        responded: stats.responded || 0,
        urgent: stats.urgent || 0,
        escalated: stats.escalated || 0,
        anonymous: stats.anonymous || 0,
        weeklyData: stats.weekly_data || [],
        groupStats: stats.group_stats || [],
        stats: stats.stats || {}
    };
};

export default prayerSlice.reducer;